import { useEffect, useMemo, useState } from "react";
import { Table, Select, Input } from "antd";
import axios from "axios";
import {
  SearchOutlined,
  TeamOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";

// NOTE: adjust these three paths if your project's shared Inventory
// components live somewhere else — same shared InventoryPageHeader /
// InventoryTable / Inventory.module.css used by OutstandingCustomers.jsx
// and the Inventory summary screens.
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";

const { Option } = Select;

/* ─── filter box theme palette (same as OutstandingCustomers / URDPurchaseDetailsRegister) ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
  amber: {
    text: "#b45309",
    bg: "#ffffff",
    border: "#fcd34d",
    ring: "rgba(180,83,9,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "PARTYNAME", label: "Party" },
  { key: "AREA", label: "Area" },
];
const secondaryFields = [{ key: "PAYAMT", label: "Payment" }];

const OutstandingDealers = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dates, setDates] = useState([null, null]);
  const [selectAreaDealer, setSelectAreaDealer] = useState("");
  const [areaDropDownDealer, setAreaDropDownDealer] = useState([]);
  const [searchParty, setSearchParty] = useState("");
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ---- data / API logic (unchanged endpoints, same fetch triggers) ---- */

  const dealerAreaDropDownAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetOutStandingDealerForCity`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      );

      const areaData = response.data;

      if (Array.isArray(areaData) && areaData.length > 0) {
        setAreaDropDownDealer(areaData);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  useEffect(() => {
    if (!dates[0] || !dates[1]) {
      return;
    }

    setLoading(true);
    setTableVisible(false);

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetOutstandingDealerData?area=${selectAreaDealer ? selectAreaDealer : "%"
        }`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
          Image: "https://image.timeserasoftware.in/WHOLESALE/9296.jpg",
        }));
        setData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching cash book details:", error);
      })
      .finally(() => setLoading(false));
  }, [dates, selectAreaDealer]);

  useEffect(() => {
    dealerAreaDropDownAPI();
  }, []);

  useEffect(() => {
    const today = new Date();
    setDates([today, today]);
  }, []);

  // Client-side Party Name search, layered on top of the server-filtered
  // (by Area) fetch — same pattern as OutstandingCustomers.jsx.
  useEffect(() => {
    let filtered = [...data];

    if (searchParty) {
      const term = searchParty.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.PARTYNAME || "").toLowerCase().includes(term),
      );
    }

    setFilteredData(
      filtered.map((item, index) => ({
        ...item,
        serialNo: index + 1,
      })),
    );
  }, [data, searchParty]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Party Name",
      dataIndex: "PARTYNAME",
      key: "PARTYNAME",
      align: "left",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "Area",
      dataIndex: "AREA",
      key: "AREA",
      align: "left",
    },
    {
      title: "Payment",
      dataIndex: "PAYAMT",
      key: "PAYAMT",
      align: "right",
      render: (value) => (
        <span className="wtBadge">{Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Image",
      dataIndex: "Image",
      key: "Image",
      align: "center",
      width: 70,
      render: (value) =>
        value ? (
          <img
            src={value}
            alt="Dealer"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              objectFit: "cover",
              border: "1px solid #e2e8f0",
            }}
          />
        ) : (
          <span style={{ color: "#94a3b8" }}>—</span>
        ),
    },
  ];

  const getTotals = (rows) => {
    return rows.reduce(
      (acc, item) => {
        acc.PAYAMT += Number(item.PAYAMT || 0);
        return acc;
      },
      { PAYAMT: 0 },
    );
  };

  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        PAYAMT: Number(item.PAYAMT || 0),
      })),
    [filteredData],
  );

  const totals = useMemo(() => getTotals(filteredData), [filteredData]);

  const areaCount = useMemo(
    () =>
      new Set(filteredData.map((item) => item.AREA).filter(Boolean)).size,
    [filteredData],
  );

  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      PARTYNAME: "",
      AREA: "",
      PAYAMT: totals.PAYAMT.toFixed(2),
      Image: "",
    },
  ];

  return (
    <div className={styles.page}>
      <style>{`
        .od-area-dropdown .ant-select-item {
          font-size: 13.5px !important;
          font-weight: 600 !important;
          padding: 8px 12px !important;
        }
        .od-area-dropdown .ant-select-item-option-selected {
          background-color: #eff6ff !important;
        }
        .od-area-dropdown .ant-select-item-option-active {
          background-color: #f0f9ff !important;
        }
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Outstanding Dealers"
        subtitle="Accounts · Outstanding Dealers"
        icon={<TeamOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          { label: "Records", value: filteredData.length, color: "blue" },
          { label: "Areas", value: areaCount, color: "orange" },
          {
            label: "Payment",
            value: totals.PAYAMT.toFixed(2),
            color: "green",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<EnvironmentOutlined />}
            label="Area"
            theme="blue"
          >
            <Select
              showSearch
              allowClear
              placeholder="Select area"
              size="middle"
              bordered={false}
              popupClassName="od-area-dropdown"
              style={{ width: 200 }}
              value={selectAreaDealer || undefined}
              onChange={(value) => setSelectAreaDealer(value)}
              filterOption={(input, option) =>
                (option?.children || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {areaDropDownDealer.map((area, index) => (
                <Option key={index} value={area.AREA}>
                  {area.AREA}
                </Option>
              ))}
            </Select>
          </FilterField>

          <FilterField
            icon={<SearchOutlined />}
            label="Party Name"
            theme="amber"
          >
            <Input
              allowClear
              size="middle"
              bordered={false}
              placeholder="Search party name…"
              value={searchParty}
              onChange={(e) => setSearchParty(e.target.value)}
              style={{ width: 200 }}
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="OutstandingDealers"
            totals={{ totalPayment: totals.PAYAMT.toFixed(2) }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No outstanding dealer records found for the selected filters"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row
            style={{ background: "#f5f5f5", fontWeight: 600 }}
          >
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.PAYAMT.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell />
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default OutstandingDealers;